/**
 * Marketplace ratings & reviews — the frontend mirror of the wire contract.
 *
 * Source of truth: `backend/modules/reviews/schemas.py` +
 * `docs/marketplace-module/reviews-api-contract.md`. A change lands in all three
 * artefacts in one pass or it is not a change.
 *
 * CONSTANTS ONLY. No shape assertions live here (see `affiliate/contract.js` for why):
 * the loaders answer a payload they cannot read with `payloadShapeError` + `LoadFailure`.
 * The fixtures in `./fixtures.js` are for TESTS and are imported by no component.
 *
 * NAMING: this wire is snake_case (it decorates the snake_case marketplace card and joins
 * the snake_case booking domain). The ONE camelCase key is the product-wide list
 * envelope's `pageSize`, which `pageQuery` below builds.
 */

// The list query builder and the settlement currency are re-exported from their single
// owners, never re-spelled: one clamp, one currency literal in the whole SPA.
import {
  SETTLEMENT_CURRENCY,
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  pageQuery,
} from "../affiliate/contract.js";

export { SETTLEMENT_CURRENCY, DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE, pageQuery };

// ── closed sets (mirror modules/reviews/schemas.py) ────────────────────────────

/** The three optional facets beside the overall star. Ordered; the backend declares a tuple. */
export const SUB_RATING_KEYS = Object.freeze(["communication", "expertise", "value"]);

export const RATING_MIN = 1;
export const RATING_MAX = 5;

export const TEXT_MAX_LEN = 1000;
export const REPLY_MAX_LEN = 1000;
export const FLAG_REASON_MAX_LEN = 500;
export const HIDE_REASON_MAX_LEN = 500;

/** Derived by the server at read time; a screen never computes one from timestamps. */
export const REQUEST_STATES = Object.freeze(["pending", "submitted", "expired"]);

/** Histogram buckets, high → low, always all present in a summary. */
export const HISTOGRAM_KEYS = Object.freeze(["5", "4", "3", "2", "1"]);

/** Program bounds the super-admin form validates locally before the server does. */
export const PRIOR_WEIGHT_MIN = 1;
export const PRIOR_WEIGHT_MAX = 50;
export const TOKEN_TTL_DAYS_MIN = 1;
export const TOKEN_TTL_DAYS_MAX = 90;

/**
 * The refusal vocabulary — CLOSED. `reviewErrorText(code, t)` maps a member to
 * `rev.error.<CODE>` (or `srev.error.<CODE>`) and an unknown one to `*.error.UNKNOWN`;
 * `detail.message` is English and is never rendered.
 */
export const REVIEW_ERROR_CODES = Object.freeze([
  "REVIEWS_DISABLED",
  "REVIEW_TOKEN_UNKNOWN",
  "REVIEW_TOKEN_EXPIRED",
  "REVIEW_ALREADY_SUBMITTED",
  "REVIEW_INVALID",
  "REVIEW_NOT_FOUND",
  "REVIEW_ALREADY_REPLIED",
  "REVIEW_ALREADY_FLAGGED",
  "REVIEW_ALREADY_HIDDEN",
  "REVIEW_NOT_HIDDEN",
  "REVIEW_PROGRAM_INVALID",
  "PRACTITIONER_NOT_FOUND",
  "RATE_LIMITED",
]);

// ── routes the SPA owns ────────────────────────────────────────────────────────

/** The query parameter the review link and the form share. */
export const TOKEN_QUERY_PARAM = "token";

/**
 * The review form's path on the TENANT origin — registered on both the platform router
 * and the tenant-host router exactly like `/gerer-rdv`. The backend builds the mail link
 * as `{tenant_public_origin}/avis?token=…`; the two spellings must agree.
 */
export const REVIEW_FORM_PATH = "/avis";

/** The platform-host profile page a card opens. */
export const practitionerProfilePath = (slug) =>
  `/marketplace/${encodeURIComponent(slug)}`;

// --- crm-module BEGIN C3.04 (marketplace attribution — write side) ---
/**
 * The query parameter a promoted marketplace link (featured strip, promoted
 * practitioner card) carries, and `lib/promotionRef.js`'s storage key — CR-B3b-1's
 * write side. Deliberately a SEPARATE code space from `affiliate/contract.js`'s
 * `REF_QUERY_PARAM`/`REF_STORAGE_KEY`: an affiliate code and a promotion id answer two
 * different questions ("who introduced this buyer" vs "which paid placement did they
 * click through"), and a buyer can carry both on the same visit.
 */
export const PROMOTION_QUERY_PARAM = "pr";
export const PROMOTION_STORAGE_KEY = "solunary_pr";
// --- crm-module END ---

// ── API paths ──────────────────────────────────────────────────────────────────

/**
 * Every path is RELATIVE to the axios `baseURL`, which already ends in `/api`
 * (`lib/api.js`). Parameterised paths are functions, so no id reaches a URL unencoded.
 */
export const REVIEW_PATHS = Object.freeze({
  // public marketplace — no auth, tenant resolved from the slug
  practitioners: "/marketplace/practitioners",
  practitioner: (slug) => `/marketplace/practitioners/${encodeURIComponent(slug)}`,
  practitionerReviews: (slug) =>
    `/marketplace/practitioners/${encodeURIComponent(slug)}/reviews`,

  // token flow — no auth, tenant resolved from the token
  request: "/reviews/request",          // GET ?token=<review token>
  submit: "/reviews",                   // POST
  byBooking: "/reviews/by-booking",     // GET ?token=<manage token>

  // tenant owner — owner session
  tenantReviews: "/tenant/reviews",
  tenantSummary: "/tenant/reviews/summary",
  tenantReply: (id) => `/tenant/reviews/${encodeURIComponent(id)}/reply`,
  tenantFlag: (id) => `/tenant/reviews/${encodeURIComponent(id)}/flag`,

  // super admin — tenants.read / tenants.write / settings.write
  adminReviews: "/super-admin/reviews",
  adminPractices: "/super-admin/reviews/practices",
  adminHide: (id) => `/super-admin/reviews/${encodeURIComponent(id)}/hide`,
  adminUnhide: (id) => `/super-admin/reviews/${encodeURIComponent(id)}/unhide`,
  adminProgram: "/super-admin/reviews/program",
  adminRebuild: "/super-admin/reviews/summaries/rebuild",
});

export default {
  SETTLEMENT_CURRENCY,
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  pageQuery,
  SUB_RATING_KEYS,
  RATING_MIN,
  RATING_MAX,
  TEXT_MAX_LEN,
  REPLY_MAX_LEN,
  FLAG_REASON_MAX_LEN,
  HIDE_REASON_MAX_LEN,
  REQUEST_STATES,
  HISTOGRAM_KEYS,
  PRIOR_WEIGHT_MIN,
  PRIOR_WEIGHT_MAX,
  TOKEN_TTL_DAYS_MIN,
  TOKEN_TTL_DAYS_MAX,
  REVIEW_ERROR_CODES,
  TOKEN_QUERY_PARAM,
  REVIEW_FORM_PATH,
  practitionerProfilePath,
  REVIEW_PATHS,
  PROMOTION_QUERY_PARAM,
  PROMOTION_STORAGE_KEY,
};
